/**
 * Debug Menu Component
 * Provides debugging tools for testing and development
 * Toggle with: Ctrl+Shift+D or click the debug button
 */

import { useState, useRef, useEffect } from 'react';
import { useGameStore } from '@/state';
import { fishingEngine, haulerManager, trapManager } from '@/systems';
import { TrapStatus, FishingState } from '@/types';
import { awardXP } from '@/types';
import { DEBUG_SHORTCUT } from '@/config/debug';
import { equipmentManager } from '@/systems/equipment/EquipmentManager';
import { saveManager } from '@/systems/persistence/SaveManager';
import { QuestTool } from './QuestTool/QuestTool';
import { FishingDebugHUD } from './FishingDebugHUD';
import { buildDemoCollectScenario, buildDemoWormDigScenario } from './MinigamePlayground';
import { CollectOverlay } from '@/components/collect';
import type { CollectScenarioConfig } from '@/components/collect';
import { WormDigOverlay } from '@/components/wormDig';
import type { WormDigConfig } from '@/types';
import { COMMIT_HASH, BUILD_TIME } from 'virtual:build-info';

interface DebugMenuProps {
  visible: boolean;
  onClose: () => void;
}

export function DebugMenu({ visible, onClose }: DebugMenuProps) {
  const [goldAmount, setGoldAmount] = useState('1000');
  const [playerXPAmount, setPlayerXPAmount] = useState('10');
  const [buddyXPAmount, setBuddyXPAmount] = useState('10');
  const [selectedBuddySlot, setSelectedBuddySlot] = useState<number>(1);
  const [haulerXPAmount, setHaulerXPAmount] = useState('10');
  const [selectedHaulerSlot, setSelectedHaulerSlot] = useState<number>(1);
  const [questToolVisible, setQuestToolVisible] = useState(false);
  const [fishingHUDVisible, setFishingHUDVisible] = useState(false);
  const [collectDemoScenario, setCollectDemoScenario] = useState<CollectScenarioConfig | null>(null);
  const [wormDigConfig, setWormDigConfig] = useState<WormDigConfig | null>(null);
  const [position, setPosition] = useState(() => ({
    x: Math.max(0, window.innerWidth - 340),
    y: 20,
  }));
  const [size, setSize] = useState({ width: 320 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  // Clamp position so menu header always stays within the viewport
  const clampPosition = (x: number, y: number, w: number) => ({
    x: Math.max(0, Math.min(x, window.innerWidth - w)),
    y: Math.max(0, Math.min(y, window.innerHeight - 48)),
  });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition(clampPosition(e.clientX - dragOffset.x, e.clientY - dragOffset.y, size.width));
      } else if (isResizing) {
        const newWidth = position.x + size.width - e.clientX;
        if (newWidth >= 250 && newWidth <= 600) {
          setSize({ width: newWidth });
          setPosition((prev) => ({ ...prev, x: e.clientX }));
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset, position, size]);

  // Re-clamp position when window is resized
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y, size.width));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [size.width]);

  if (!visible) return null;

  const handleHeaderMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'H2' || (e.target as HTMLElement).tagName === 'SPAN') {
      const rect = menuRef.current?.getBoundingClientRect();
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
        setIsDragging(true);
      }
    }
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleInstaFish = () => {
    const state = useGameStore.getState();

    // Check if inventory is full
    if (state.playerState.inventory.currentCount >= state.playerState.inventory.capacity) {
      alert('Inventory is full! Sell some fish first.');
      return;
    }

    // Use the fishing engine's instant fish method
    // This ensures we ALWAYS use the exact same logic as normal fishing
    // (stats, bonuses, discovery tracking, XP, level-ups, saves, etc.)
    fishingEngine.instantFish();
  };

  const handleInstaMoney = () => {
    const amount = parseInt(goldAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid positive number');
      return;
    }

    const state = useGameStore.getState();
    state.addCurrency(amount);
    console.log(`💰 [DEBUG] Added ${amount} gold`);
  };

  const handleInstaPlayerXP = () => {
    const amount = parseInt(playerXPAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid positive number');
      return;
    }

    const state = useGameStore.getState();
    if (!state.playerState.progression) {
      console.error('[DEBUG] ERROR: progression is undefined!');
      alert('ERROR: Progression system not initialized!');
      return;
    }

    const oldLevel = state.playerState.progression.progress.level;
    const oldXP = state.playerState.progression.progress.currentXP;

    useGameStore.setState((draft) => {
      const newProgression = awardXP(draft.playerState.progression, amount);
      draft.playerState.progression = newProgression;
    });

    const newState = useGameStore.getState();
    const newLevel = newState.playerState.progression.progress.level;

    if (newLevel > oldLevel) {
      console.log(
        `🎉 [DEBUG] PLAYER LEVEL UP! ${oldLevel} → ${newLevel} (+${amount} XP) | ` +
        `Stat points: ${newState.playerState.progression.progress.unspentStatPoints}`
      );
      // Recalculate stats after level-up
      equipmentManager.recalculateStats();
    } else {
      console.log(
        `⬆️ [DEBUG] Added ${amount} XP to player | ` +
        `Level ${newLevel}: ${oldXP} → ${newState.playerState.progression.progress.currentXP}/${newState.playerState.progression.progress.xpToNextLevel}`
      );
    }

    // Save changes
    saveManager.save();
  };

  const handleInstaBuddyXP = () => {
    const amount = parseInt(buddyXPAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid positive number');
      return;
    }

    const state = useGameStore.getState();
    const buddy = state.playerState.fishingBuddies.find((b) => b.slotNumber === selectedBuddySlot);

    if (!buddy || !buddy.hired) {
      alert(`Buddy ${selectedBuddySlot} is not hired!`);
      return;
    }

    if (!buddy.progression) {
      console.error('[DEBUG] ERROR: buddy progression is undefined!');
      alert('ERROR: Buddy progression not initialized!');
      return;
    }

    const oldLevel = buddy.progression.progress.level;
    const oldXP = buddy.progression.progress.currentXP;

    useGameStore.setState((draft) => {
      const targetBuddy = draft.playerState.fishingBuddies.find((b) => b.slotNumber === selectedBuddySlot);
      if (targetBuddy && targetBuddy.progression) {
        const newProgression = awardXP(targetBuddy.progression, amount);
        targetBuddy.progression = newProgression;
      }
    });

    const newState = useGameStore.getState();
    const updatedBuddy = newState.playerState.fishingBuddies.find((b) => b.slotNumber === selectedBuddySlot);
    const newLevel = updatedBuddy!.progression!.progress.level;

    if (newLevel > oldLevel) {
      console.log(
        `🎉 [DEBUG] BUDDY ${selectedBuddySlot} LEVEL UP! ${oldLevel} → ${newLevel} (+${amount} XP) | ` +
        `Stat points: ${updatedBuddy!.progression!.progress.unspentStatPoints}`
      );
    } else {
      console.log(
        `⬆️ [DEBUG] Added ${amount} XP to Buddy ${selectedBuddySlot} | ` +
        `Level ${newLevel}: ${oldXP} → ${updatedBuddy!.progression!.progress.currentXP}/${updatedBuddy!.progression!.progress.xpToNextLevel}`
      );
    }

    // Save changes
    saveManager.save();
  };

  const handleInstaHireHauler = () => {
    const slotNumber = selectedHaulerSlot;
    if (haulerManager.hireHauler(slotNumber)) {
      console.log(`🚚 [DEBUG] Hired hauler ${slotNumber}`);
      saveManager.save();
    } else {
      alert(`Failed to hire hauler ${slotNumber}. Check console for details.`);
    }
  };

  const handleInstaHaulerXP = () => {
    const amount = parseInt(haulerXPAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid positive number');
      return;
    }

    const state = useGameStore.getState();
    const hauler = state.playerState.haulerBuddies.find((h) => h.slotNumber === selectedHaulerSlot);

    if (!hauler || !hauler.hired) {
      alert(`Hauler ${selectedHaulerSlot} is not hired!`);
      return;
    }

    if (!hauler.progression) {
      console.error('[DEBUG] ERROR: hauler progression is undefined!');
      alert('ERROR: Hauler progression not initialized!');
      return;
    }

    const oldLevel = hauler.progression.progress.level;
    const oldXP = hauler.progression.progress.currentXP;

    useGameStore.setState((draft) => {
      const targetHauler = draft.playerState.haulerBuddies.find((h) => h.slotNumber === selectedHaulerSlot);
      if (targetHauler && targetHauler.progression) {
        const newProgression = awardXP(targetHauler.progression, amount);
        targetHauler.progression = newProgression;
      }
    });

    const newState = useGameStore.getState();
    const updatedHauler = newState.playerState.haulerBuddies.find((h) => h.slotNumber === selectedHaulerSlot);
    const newLevel = updatedHauler!.progression!.progress.level;

    if (newLevel > oldLevel) {
      console.log(
        `🎉 [DEBUG] HAULER ${selectedHaulerSlot} LEVEL UP! ${oldLevel} → ${newLevel} (+${amount} XP) | ` +
        `Stat points: ${updatedHauler!.progression!.progress.unspentStatPoints}`
      );
    } else {
      console.log(
        `⬆️ [DEBUG] Added ${amount} XP to Hauler ${selectedHaulerSlot} | ` +
        `Level ${newLevel}: ${oldXP} → ${updatedHauler!.progression!.progress.currentXP}/${updatedHauler!.progression!.progress.xpToNextLevel}`
      );
    }

    // Save changes
    saveManager.save();
  };

  const handleResetProgress = () => {
    if (confirm('Are you sure you want to reset ALL progress? This cannot be undone!')) {
      console.log('🗑️ [DEBUG] Resetting game progress...');
      localStorage.clear();
      window.location.reload();
    }
  };

  const handlePlayCollectDemo = () => {
    const scenario = buildDemoCollectScenario(() => {
      setCollectDemoScenario(null);
    });
    setCollectDemoScenario(scenario);
  };

  const handlePlayWormDigDemo = () => {
    const config = buildDemoWormDigScenario((result) => {
      console.log('🪱 [DEBUG] Worm dig complete:', result);
      setWormDigConfig(null);
    });
    setWormDigConfig(config);
  };

  const handlePlaySweetSpotDemo = () => {
    const state = useGameStore.getState();

    if (state.fishingState.status !== FishingState.IDLE) {
      alert('Already fishing! Wait for the current cast to finish.');
      return;
    }

    if (state.playerState.inventory.currentCount >= state.playerState.inventory.capacity) {
      alert('Inventory is full! Sell some fish first.');
      return;
    }

    state.setCurrentTab('fishing');
    fishingEngine.startFishing();
    console.log('🎯 [DEBUG] Started sweet spot demo — switch to fishing tab');
  };

  return (
    <div
      ref={menuRef}
      className="fixed bg-gray-900 border-2 border-red-500 shadow-2xl z-50 flex flex-col rounded-lg overflow-hidden"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        maxHeight: '80vh',
      }}
    >
      {/* Resize Handle (left edge) */}
      <div
        onMouseDown={handleResizeMouseDown}
        className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-red-500 transition-colors"
        style={{ zIndex: 10 }}
      />

      {/* Header - Draggable */}
      <div
        onMouseDown={handleHeaderMouseDown}
        className="flex items-center justify-between p-3 border-b-2 border-red-500/30 bg-gray-950 cursor-move select-none"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🔧</span>
          <h2 className="text-sm font-bold text-red-500">Debug Tools</h2>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white text-lg px-2 cursor-pointer"
          title="Close"
        >
          ✕
        </button>
      </div>

      {/* Debug Tools - Scrollable */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* Insta Fish */}
        <div className="bg-gray-800 p-3 rounded-lg border border-cyan-500/30">
          <h3 className="font-bold text-cyan-400 mb-2 text-sm">⚡ Insta Fish</h3>
          <p className="text-xs text-gray-400 mb-2">
            Catch fish instantly (awards XP)
          </p>
          <button
            onClick={handleInstaFish}
            className="w-full py-2 px-3 bg-cyan-600 hover:bg-cyan-700 rounded-lg font-bold text-sm transition-colors"
          >
            Catch Now
          </button>
        </div>

        {/* Insta Money */}
        <div className="bg-gray-800 p-3 rounded-lg border border-yellow-500/30">
          <h3 className="font-bold text-yellow-400 mb-2 text-sm">💰 Insta Money</h3>
          <p className="text-xs text-gray-400 mb-2">
            Add gold to wallet
          </p>
          <div className="flex gap-2">
            <input
              type="number"
              value={goldAmount}
              onChange={(e) => setGoldAmount(e.target.value)}
              className="flex-1 px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-yellow-500"
              placeholder="Amount"
            />
            <button
              onClick={handleInstaMoney}
              className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 rounded font-bold text-sm transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Insta Player XP */}
        <div className="bg-gray-800 p-3 rounded-lg border border-purple-500/30">
          <h3 className="font-bold text-purple-400 mb-2 text-sm">⬆️ Insta Player XP</h3>
          <p className="text-xs text-gray-400 mb-2">
            Add XP to player (for testing leveling)
          </p>
          <div className="flex gap-2">
            <input
              type="number"
              value={playerXPAmount}
              onChange={(e) => setPlayerXPAmount(e.target.value)}
              className="flex-1 px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-purple-500"
              placeholder="XP Amount"
            />
            <button
              onClick={handleInstaPlayerXP}
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded font-bold text-sm transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Insta Buddy XP */}
        <div className="bg-gray-800 p-3 rounded-lg border border-indigo-500/30">
          <h3 className="font-bold text-indigo-400 mb-2 text-sm">🧑‍🤝‍🧑 Insta Buddy XP</h3>
          <p className="text-xs text-gray-400 mb-2">
            Add XP to a buddy
          </p>
          <div className="flex gap-2 mb-2">
            <select
              value={selectedBuddySlot}
              onChange={(e) => setSelectedBuddySlot(parseInt(e.target.value, 10))}
              className="flex-1 px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-indigo-500"
            >
              <option value={1}>Buddy 1</option>
              <option value={2}>Buddy 2</option>
              <option value={3}>Buddy 3</option>
            </select>
          </div>
          <div className="flex gap-2">
            <input
              type="number"
              value={buddyXPAmount}
              onChange={(e) => setBuddyXPAmount(e.target.value)}
              className="flex-1 px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-indigo-500"
              placeholder="XP Amount"
            />
            <button
              onClick={handleInstaBuddyXP}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 rounded font-bold text-sm transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Insta Hire Hauler */}
        <div className="bg-gray-800 p-3 rounded-lg border border-orange-500/30">
          <h3 className="font-bold text-orange-400 mb-2 text-sm">🚚 Insta Hire Hauler</h3>
          <p className="text-xs text-gray-400 mb-2">
            Instantly hire a hauler (bypasses cost)
          </p>
          <div className="flex gap-2">
            <select
              value={selectedHaulerSlot}
              onChange={(e) => setSelectedHaulerSlot(parseInt(e.target.value, 10))}
              className="flex-1 px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-orange-500"
            >
              <option value={1}>Hauler 1</option>
              <option value={2}>Hauler 2</option>
            </select>
            <button
              onClick={handleInstaHireHauler}
              className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 rounded font-bold text-sm transition-colors"
            >
              Hire
            </button>
          </div>
        </div>

        {/* Insta Hauler XP */}
        <div className="bg-gray-800 p-3 rounded-lg border border-orange-500/30">
          <h3 className="font-bold text-orange-400 mb-2 text-sm">🚚 Insta Hauler XP</h3>
          <p className="text-xs text-gray-400 mb-2">
            Add XP to a hauler
          </p>
          <div className="flex gap-2 mb-2">
            <select
              value={selectedHaulerSlot}
              onChange={(e) => setSelectedHaulerSlot(parseInt(e.target.value, 10))}
              className="flex-1 px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-orange-500"
            >
              <option value={1}>Hauler 1</option>
              <option value={2}>Hauler 2</option>
            </select>
          </div>
          <div className="flex gap-2">
            <input
              type="number"
              value={haulerXPAmount}
              onChange={(e) => setHaulerXPAmount(e.target.value)}
              className="flex-1 px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-orange-500"
              placeholder="XP Amount"
            />
            <button
              onClick={handleInstaHaulerXP}
              className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 rounded font-bold text-sm transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Fishing HUD */}
        <div className="bg-gray-800 p-3 rounded-lg border border-green-500/30">
          <h3 className="font-bold text-green-400 mb-2 text-sm">🎣 Fishing HUD</h3>
          <p className="text-xs text-gray-400 mb-2">
            Real-time sweet spot debug overlay
          </p>
          <button
            onClick={() => setFishingHUDVisible((v) => !v)}
            className={`w-full py-2 px-3 rounded-lg font-bold text-sm transition-colors ${
              fishingHUDVisible
                ? 'bg-green-700 hover:bg-green-800 ring-2 ring-green-400'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {fishingHUDVisible ? 'HUD Active — Click to Hide' : 'Show Fishing HUD'}
          </button>
        </div>

        {/* Minigame Playground */}
        <div className="bg-gray-800 p-3 rounded-lg border border-pink-500/30">
          <h3 className="font-bold text-pink-400 mb-2 text-sm">🎮 Minigame Playground</h3>
          <p className="text-xs text-gray-400 mb-2">
            Test minigames using real game code
          </p>
          <div className="space-y-2">
            <button
              onClick={handlePlayCollectDemo}
              className="w-full py-2 px-3 bg-pink-600 hover:bg-pink-700 rounded-lg font-bold text-sm transition-colors"
            >
              Play Collect Demo
            </button>
            <button
              onClick={handlePlaySweetSpotDemo}
              className="w-full py-2 px-3 bg-pink-600 hover:bg-pink-700 rounded-lg font-bold text-sm transition-colors"
            >
              Start Sweet Spot Cast
            </button>
            <button
              onClick={handlePlayWormDigDemo}
              className="w-full py-2 px-3 bg-amber-700 hover:bg-amber-800 rounded-lg font-bold text-sm transition-colors"
            >
              Play Worm Dig
            </button>
          </div>
        </div>

        {/* Speed Up Trap */}
        <div className="bg-gray-800 p-3 rounded-lg border border-teal-500/30">
          <h3 className="font-bold text-teal-400 mb-2 text-sm">🪤 Speed Up Trap</h3>
          <p className="text-xs text-gray-400 mb-2">
            Instantly complete trap soak timer
          </p>
          <button
            onClick={() => {
              const trapState = useGameStore.getState().playerState.trapState;
              if (trapState.status !== TrapStatus.SOAKING) {
                alert('Trap is not soaking right now!');
                return;
              }
              useGameStore.setState((draft) => {
                draft.playerState.trapState.readyAt = Date.now() - 1;
              });
              // Trigger tick to process the transition
              (trapManager as any).tick();
              console.log('🪤 [DEBUG] Trap soak completed instantly');
            }}
            className="w-full py-2 px-3 bg-teal-600 hover:bg-teal-700 rounded-lg font-bold text-sm transition-colors"
          >
            Complete Soak Now
          </button>
        </div>

        {/* Quest Tool */}
        <div className="bg-gray-800 p-3 rounded-lg border border-blue-500/30">
          <h3 className="font-bold text-blue-400 mb-2 text-sm">📋 Quest Tool</h3>
          <p className="text-xs text-gray-400 mb-2">
            Create, edit, and manage quests
          </p>
          <button
            onClick={() => setQuestToolVisible(true)}
            className="w-full py-2 px-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-bold text-sm transition-colors"
          >
            Open Quest Tool
          </button>
        </div>

        {/* Reset Progress */}
        <div className="bg-gray-800 p-3 rounded-lg border-2 border-red-600/50">
          <h3 className="font-bold text-red-400 mb-2 text-sm">🗑️ Reset Progress</h3>
          <p className="text-xs text-gray-400 mb-2">
            Clear all game data
          </p>
          <button
            onClick={handleResetProgress}
            className="w-full py-2 px-3 bg-red-600 hover:bg-red-700 rounded-lg font-bold text-sm transition-colors"
          >
            Reset Game
          </button>
        </div>
      </div>

      {/* Footer Info */}
      <div className="p-2 border-t border-gray-700 bg-gray-950">
        <p className="text-xs text-green-500 text-center font-mono">
          Build: {COMMIT_HASH} ({new Date(BUILD_TIME).toLocaleString()})
        </p>
        <p className="text-xs text-gray-500 text-center mt-1">
          Drag to move • Left edge to resize
        </p>
        <p className="text-xs text-gray-600 text-center mt-1">
          Toggle: {DEBUG_SHORTCUT.ctrl ? 'Ctrl+' : ''}{DEBUG_SHORTCUT.shift ? 'Shift+' : ''}{DEBUG_SHORTCUT.alt ? 'Alt+' : ''}{DEBUG_SHORTCUT.key}
        </p>
      </div>

      {/* Quest Tool Modal */}
      {questToolVisible && (
        <QuestTool onClose={() => setQuestToolVisible(false)} />
      )}

      {/* Fishing Debug HUD (renders outside DebugMenu, fixed position) */}
      {fishingHUDVisible && (
        <FishingDebugHUD onClose={() => setFishingHUDVisible(false)} />
      )}

      {/* Collect Demo Overlay */}
      <CollectOverlay scenario={collectDemoScenario} />

      {/* Worm Dig Demo Overlay */}
      <WormDigOverlay config={wormDigConfig} />
    </div>
  );
}
